// Home v2 — "hide token" confirmation popup (mobile-web build). Mirrors the
// extension's HideTokenModal: confirm before hiding, with a "Don't ask me
// again" opt-out that persists in storage (handled by the parent hook).
import React, { useEffect, useState } from 'react'
import { Pressable, View } from 'react-native'

import Checkbox from '@common/components/Checkbox'
import Text from '@common/components/Text'
import useTheme from '@common/hooks/useTheme'
import { Mode } from '@web/contexts/appModeContext'

import { getModeAccent, RADII } from '../tokens'

type Props = {
  visible: boolean
  mode: Mode
  onCancel: () => void
  onConfirm: (doNotAskAgain: boolean) => void
}

const HideTokenConfirmModal: React.FC<Props> = ({ visible, mode, onCancel, onConfirm }) => {
  const { theme } = useTheme()
  const accent = getModeAccent(theme, mode)
  const [doNotAskAgain, setDoNotAskAgain] = useState(false)

  // Reset the checkbox each time the popup is re-opened.
  useEffect(() => {
    if (visible) setDoNotAskAgain(false)
  }, [visible])

  if (!visible) return null

  return (
    <View
      style={{
        position: 'fixed' as any,
        inset: 0 as any,
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Pressable
        style={{ position: 'absolute' as any, inset: 0 as any, backgroundColor: 'rgba(0,0,0,0.5)' }}
        onPress={onCancel}
      />
      <View
        style={{
          width: 380,
          maxWidth: '92%',
          gap: 20,
          backgroundColor: theme.homeCardBackground as string,
          borderWidth: 1,
          borderColor: theme.homeCardBorder as string,
          borderRadius: RADII.lg,
          padding: 24
        }}
      >
        <Text fontSize={18} weight="semiBold">
          Are you sure you want to hide this token?
        </Text>
        <Text fontSize={14} appearance="secondaryText">
          You can always unhide it from the Settings menu {'>'} Custom tokens.
        </Text>

        <Checkbox
          value={doNotAskAgain}
          onValueChange={setDoNotAskAgain}
          label="Don't ask me again"
          style={{ marginBottom: 0 }}
        />

        <View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 12 }}>
          <Pressable
            onPress={onCancel}
            style={{
              height: 44,
              paddingHorizontal: 24,
              borderRadius: RADII.pill,
              borderWidth: 1,
              borderColor: accent,
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Text fontSize={15} weight="medium" color={accent}>
              Cancel
            </Text>
          </Pressable>
          <Pressable
            onPress={() => onConfirm(doNotAskAgain)}
            style={{
              height: 44,
              paddingHorizontal: 24,
              borderRadius: RADII.pill,
              backgroundColor: accent,
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Text fontSize={15} weight="semiBold" color="#fff">
              Yes, hide it!
            </Text>
          </Pressable>
        </View>
      </View>
    </View>
  )
}

export default HideTokenConfirmModal
